import { KEY_ORDER } from '@models/key-order';
import IPaginatedParams from '@models/paginated-params';
import SettingType from '@settings/models/setting-type.enum';

const BASE_ROOT = process.env.REACT_APP_BACKEND_URI;
const ADMIN_ROOT = `${BASE_ROOT}/admin`;

const ENTERPRISES = `${ADMIN_ROOT}/enterprises`;
const CV_VIDEOS = `${ADMIN_ROOT}/cv-videos`;
const VIDEO_STEPS = `${ADMIN_ROOT}/video-steps`;
const AUTH = `${ADMIN_ROOT}/auth`;
const USER = `${ADMIN_ROOT}/users`;
const JOBS = `${ADMIN_ROOT}/jobs`;
const CANDIDATES = `${ADMIN_ROOT}/candidates`;
const CITIES = `${BASE_ROOT}/cities`;
const ROME_DOMAIN = `${BASE_ROOT}/rome-domains`;
const ROME_DOMAIN_ADMIN = `${ADMIN_ROOT}/rome-domains`;
const COUNTRIES = `${BASE_ROOT}/countries`;
const ESTABLISHMENT = `${ADMIN_ROOT}/establishments`;
const NOTIFICATION_TOKENS = `${BASE_ROOT}/notification-tokens`;
const SETTINGS = `${ADMIN_ROOT}/settings`;
const NOTIFICATIONS = `${ADMIN_ROOT}/notifications`;
const USER_NOTIFICATIONS = `${ADMIN_ROOT}/user-notif-com/notifications`;
export const NOTIFICATION_EVENTS = `${BASE_ROOT}/notification-events`;

class Enterprise {
  public static base = ENTERPRISES;

  public static getEnterprises = ({
    page,
    limit,
    order,
    orderBy,
  }: IPaginatedParams): string =>
    `${ENTERPRISES}/filter-criteria?order=${order ?? KEY_ORDER.DESC}${`&page=${
      page ?? 1
    }`}${limit !== undefined ? `&take=${limit}` : ''}${
      orderBy !== undefined ? `&orderBy=${orderBy}` : ''
    }`;

  public static getEnterpriseById = (id: string | undefined): string =>
    `${ENTERPRISES}/${id}`;

  public static enableEnterpriseById = (id: string): string =>
    `${ENTERPRISES}/${id}/enable`;

  public static disableEnterpriseById = (id: string): string =>
    `${ENTERPRISES}/${id}/disable`;

  public static createEnterprise = (): string => `${ENTERPRISES}`;

  public static editEnterprise = (id: string): string => `${ENTERPRISES}/${id}`;

  public static getEnterprisesSuggestions = (
    paginatedParams: IPaginatedParams,
  ): string => {
    const { limit, page, query, order } = paginatedParams;
    return `${ENTERPRISES}?order=${
      order ?? KEY_ORDER.DESC
    }&page=${page}&take=${limit}${query && `&keyword=${query}`}`;
  };

  public static updateEnterpriseAllowedActions = (id: string | undefined) =>
    `${ENTERPRISES}/${id}/update-consumption-allowed-actions`;

  public static getEnterpriseCreditsConsumption = (
    id: string | undefined,
    params: IPaginatedParams,
  ): string => {
    const { limit, page, order } = params;
    return `${ADMIN_ROOT}/credits-consumption/enterprise/${id}?order=${
      order ?? KEY_ORDER.DESC
    }&page=${page}&take=${limit}`;
  };

  public static getEnterpriseCreditsTopUp = (
    id: string | undefined,
    params: IPaginatedParams,
  ): string => {
    const { limit, page, order } = params;
    return `${ADMIN_ROOT}/credits-topup/enterprise/${id}?order=${
      order ?? KEY_ORDER.DESC
    }&page=${page}&take=${limit}`;
  };

  public static exportCsvConsumptions = (id: string | undefined): string =>
    `${ADMIN_ROOT}/credits-consumption/enterprise/${id}/export-csv`;

  public static exportCsvTopUp = (id: string | undefined): string =>
    `${ADMIN_ROOT}/credits-topup/enterprise/${id}/export-csv`;
}
class CVVideos {
  public static base = CV_VIDEOS;

  public static getCVVideos = ({
    page,
    limit,
    order,
    orderBy,
  }: IPaginatedParams): string =>
    `${CV_VIDEOS}/filter-criteria?order=${order ?? KEY_ORDER.DESC}${`&page=${
      page ?? 1
    }`}${limit !== undefined ? `&take=${limit}` : ''}${
      orderBy !== undefined ? `&orderBy=${orderBy}` : ''
    }`;

  public static getCVVideoId = (id: string | undefined): string =>
    `${CV_VIDEOS}/${id}`;

  public static putCVVideoId = (id: string | undefined): string =>
    `${CV_VIDEOS}/${id}`;

  public static contentModeration = (id: string | undefined): string =>
    `${CV_VIDEOS}/content-moderation/${id}`;

  public static speechModerationById = (id: string | undefined): string =>
    `${CV_VIDEOS}/speech-moderation/${id}`;

  public static textModerationById = (id: string | undefined): string =>
    `${CV_VIDEOS}/text-moderation/${id}`;
}

class Jobs {
  public static base = JOBS;

  public static getJobs = ({
    page,
    limit,
    order,
    orderBy,
  }: IPaginatedParams): string =>
    `${JOBS}/filter-criteria?order=${order ?? KEY_ORDER.DESC}${`&page=${
      page ?? 1
    }`}${limit !== undefined ? `&take=${limit}` : ''}${
      orderBy !== undefined ? `&orderBy=${orderBy}` : ''
    }`;

  public static getJobById = (id: string | undefined): string =>
    `${JOBS}/${id}`;

  public static updateJobById = (id: string | undefined): string =>
    `${JOBS}/${id}`;

  public static disableJobById = (id: string | undefined): string =>
    `${JOBS}/${id}/disable`;

  public static enableJobById = (id: string | undefined): string =>
    `${JOBS}/${id}/enable`;
}

class VideoSteps {
  public static base = VIDEO_STEPS;
  public static getVideoSteps = (): string => VIDEO_STEPS;
  public static createVideoStep = (): string => VIDEO_STEPS;
  public static updateVideosOrder = (): string => `${VIDEO_STEPS}/reorder`;
  public static updateVideoStepById = (id: string): string =>
    `${VIDEO_STEPS}/${id}`;

  public static disableVideoStepById = (id: string): string =>
    `${VIDEO_STEPS}/${id}/disable`;

  public static enableVideoStepById = (id: string): string =>
    `${VIDEO_STEPS}/${id}/enable`;

  public static editVideoStepById = (id: string): string =>
    `${VIDEO_STEPS}/${id}`;
}

class Candidates {
  public static base = CANDIDATES;

  public static getCandidates = ({
    page,
    limit,
    order,
    orderBy,
  }: IPaginatedParams): string =>
    `${CANDIDATES}/filter-criteria?order=${order ?? KEY_ORDER.DESC}${`&page=${
      page ?? 1
    }`}${limit !== undefined ? `&take=${limit}` : ''}${
      orderBy !== undefined ? `&orderBy=${orderBy}` : ''
    }`;

  public static getCandidateById = (id: string | undefined): string =>
    `${CANDIDATES}/${id}`;

  public static disableCandidateById = (id: string | undefined): string =>
    `${CANDIDATES}/${id}/disable`;

  public static enableCandidateById = (id: string | undefined): string =>
    `${CANDIDATES}/${id}/enable`;
}

class Establishment {
  public static base = ESTABLISHMENT;

  public static getEstablishment = ({
    page,
    limit,
    order,
    orderBy,
  }: IPaginatedParams): string =>
    `${ESTABLISHMENT}/filter-criteria?order=${
      order ?? KEY_ORDER.DESC
    }${`&page=${page ?? 1}`}${limit !== undefined ? `&take=${limit}` : ''}${
      orderBy !== undefined ? `&orderBy=${orderBy}` : ''
    }`;

  public static getEstablishmentById = (id: string | undefined): string =>
    `${ESTABLISHMENT}/${id}`;

  public static enableEstablishmentById = (id: string): string =>
    `${ESTABLISHMENT}/${id}/enable`;

  public static disableEstablishmentById = (id: string): string =>
    `${ESTABLISHMENT}/${id}/disable`;

  public static createEstablishment = (): string => `${ESTABLISHMENT}`;

  public static editEstablishment = (id: string | undefined): string =>
    `${ESTABLISHMENT}/${id}`;

  public static getEstablishmentsSuggestions = (
    paginatedParams: IPaginatedParams,
  ): string => {
    const { limit, page, query, order } = paginatedParams;
    return `${ESTABLISHMENT}?order=${
      order ?? KEY_ORDER.DESC
    }&page=${page}&take=${limit}${query && `&keyword=${query}`}`;
  };

  public static getEstablishmentsSuggestionsByEnterpriseId = (
    paginatedParams: IPaginatedParams,
    enterpriseId: string,
  ): string => {
    const { limit, page, query, order } = paginatedParams;
    return `${ESTABLISHMENT}/enterprise/${enterpriseId}?order=${
      order ?? KEY_ORDER.DESC
    }&page=${page}&take=${limit}${query && `&keyword=${query}`}`;
  };

  public static getEstablishmentByEnterpriseIdWithThreshold = (
    enterpriseId: string,
  ): string => {
    return `${ESTABLISHMENT}/enterprise-with-credit-threshold/${enterpriseId}`;
  };

  public static getEstablishmentCreditsConsumption = (
    id: string | undefined,
    params: IPaginatedParams,
  ): string => {
    const { limit, page, order } = params;
    return `${ADMIN_ROOT}/credits-consumption/establishment/${id}?order=${
      order ?? KEY_ORDER.DESC
    }&page=${page}&take=${limit}`;
  };

  public static getEstablishmentCreditsTopup = (
    id: string | undefined,
    params: IPaginatedParams,
  ): string => {
    const { limit, page, order } = params;
    return `${ADMIN_ROOT}/credits-topup/establishment/${id}?order=${
      order ?? KEY_ORDER.DESC
    }&page=${page}&take=${limit}`;
  };

  public static exportCsvConsumptions = (id: string | undefined): string =>
    `${ADMIN_ROOT}/credits-consumption/establishment/${id}/export-csv`;

  public static exportCsvTopUp = (id: string | undefined): string =>
    `${ADMIN_ROOT}/credits-topup/establishment/${id}/export-csv`;
}

class Auth {
  public static base = AUTH;
  public static admin = `${AUTH}/login`;
  public static refreshAccessToken = `${AUTH}/refresh`;
  public static getCurrentUser = `${ADMIN_ROOT}/auth/me`; // temp waiting for creating /me for the admin API
}

class User {
  public static base = User;
  public static getUserById = (id: string | undefined): string =>
    `${USER}/${id}`;

  public static editUserById = (
    id: string | undefined,
    userType: 'enterprise' | 'establishment',
  ): string =>
    `${USER}/${
      userType === 'establishment' ? 'establishment' : 'enterprise'
    }/${id}`;

  public static getUsers = ({
    page,
    limit,
    order,
    orderBy,
  }: IPaginatedParams): string =>
    `${USER}/filter-criteria?order=${order ?? KEY_ORDER.DESC}${`&page=${
      page ?? 1
    }`}${limit !== undefined ? `&take=${limit}` : ''}${
      orderBy !== undefined ? `&orderBy=${orderBy}` : ''
    }`;

  public static createUser = (type: 'enterprise' | 'establishment'): string =>
    `${USER}/${type === 'establishment' ? 'establishment' : 'enterprise'}`;

  public static enableUserById = (id: string): string => `${USER}/${id}/enable`;

  public static disableUserById = (id: string): string =>
    `${USER}/${id}/disable`;

  public static getUserEstablishmentsEnterprise = (
    id: string,
    keyword?: string,
  ) => `${USER}/enterprise/${id}${keyword && `?keyword=${keyword}`}`;

  public static getUserEstablishment = (id: string, keyword?: string) =>
    `${USER}/establishment/${id}${keyword && `?keyword=${keyword}`}`;

  public static forgotPassword = (username: string): string =>
    `${USER}/${username}/forgot-password`;

  public static resetPassword = (token: string): string =>
    `${USER}/${token}/reset-password`;
}

class City {
  public static getCities = (paginatedParams: IPaginatedParams): string => {
    const { limit, page, query, order } = paginatedParams;
    return `${CITIES}?order=${
      order ?? KEY_ORDER.DESC
    }&page=${page}&take=${limit}${query && `&keyword=${query}`}`;
  };
}

class Country {
  public static getCountries = (paginatedParams: IPaginatedParams): string => {
    const { limit, page, query, order } = paginatedParams;
    return `${COUNTRIES}?order=${
      order ?? KEY_ORDER.DESC
    }&page=${page}&take=${limit}${query && `&keyword=${query}`}`;
  };
}
class RomeDomain {
  public static base = ROME_DOMAIN;
  public static getRomeDomains = (query: string): string =>
    `${ROME_DOMAIN}?${query ? `&keyword=${query}&take=10&page=1` : ''}`;

  public static reOrder = () => `${ROME_DOMAIN_ADMIN}/reorder`;
  public static getAll = () => `${ROME_DOMAIN_ADMIN}`;
}
class NotificationToken {
  public static base = NOTIFICATION_TOKENS;
  public static create = `${this.base}`;
}

class Notifications {
  public static base = NOTIFICATIONS;
  public static getAll = ({ page, limit }: IPaginatedParams, userId: string) =>
    `${this.base}/user/${userId}?page=${page}&take=${limit}`;

  public static makeAsReadByNotificationId = (id: string) =>
    `${this.base}/set-read/${id}`;

  public static getUserNotifications = (id: string | undefined) =>
    `${USER_NOTIFICATIONS}/user/${id}`;

  public static disableNotification = (id: string | undefined) =>
    `${USER_NOTIFICATIONS}/set-disabled/user/${id}`;

  public static makeAllAsReadByUserId = (id: string | undefined) =>
    `${this.base}/set-all-read/user/${id}`;
}

class Setting {
  public static base = SETTINGS;
  public static getSettingByType = (type: SettingType): string =>
    `${this.base}/type/${type}`;

  public static updateSettings = (): string => this.base;
}

class Credit {
  public static base = ADMIN_ROOT;
  public static creditsTopup = `${this.base}/credits-topup`;
  public static creditsConsumption = `${this.base}/credits-consumption`;

  public static createTopUps: string = `${this.creditsTopup}`;
  public static deleteTopUps = (topUpId: string) =>
    `${this.creditsTopup}/${topUpId}`;

  public static getTopUps = ({
    page,
    limit,
    order,
    orderBy,
  }: IPaginatedParams): string =>
    `${this.creditsTopup}/filter-criteria?order=${
      order ?? KEY_ORDER.DESC
    }${`&page=${page ?? 1}`}${limit !== undefined ? `&take=${limit}` : ''}${
      orderBy !== undefined ? `&orderBy=${orderBy}` : ''
    }`;

  public static getCreditsConsumption = ({
    page,
    limit,
    order,
    orderBy,
  }: IPaginatedParams): string =>
    `${this.creditsConsumption}/filter-criteria?order=${
      order ?? KEY_ORDER.DESC
    }${`&page=${page ?? 1}`}${limit !== undefined ? `&take=${limit}` : ''}${
      orderBy !== undefined ? `&orderBy=${orderBy}` : ''
    }`;

  public static getDetailsTopUp = (topUpId: string) =>
    `${this.creditsTopup}/${topUpId}`;
}
export default class EndPoints {
  public static auth = Auth;
  public static user = User;
  public static enterprise = Enterprise;
  public static city = City;
  public static romeDomain = RomeDomain;
  public static country = Country;
  public static establishment = Establishment;
  public static cvVideos = CVVideos;
  public static notificationToken = NotificationToken;
  public static notifications = Notifications;
  public static jobs = Jobs;
  public static candidates = Candidates;
  public static videoSteps = VideoSteps;
  public static setting = Setting;
  public static credit = Credit;
}
